/**
 * RFQ Projects Page Logic — v2
 * Renders active RFQ (editable) + submitted history (read-only)
 * Reads config from #rfq-init-data (injected by rfq-init.liquid)
 *
 * FIX v2: All logic deferred to DOMContentLoaded because rfq-init.liquid
 *         outputs #rfq-init-data AFTER section scripts in the HTML.
 */
(function () {
  'use strict';

  function boot() {
    /* ── config ──────────────────────────────────────────── */
    const cfgEl = document.getElementById('rfq-init-data');
    if (!cfgEl) return;
    const CFG = JSON.parse(cfgEl.textContent);
    const t = CFG.t || {};
    const syncRoute = CFG.routes && CFG.routes.sync;
    const loggedIn = CFG.customer_logged_in;
    const STORAGE_KEY = 'rfq_items';

    /* ── DOM refs ────────────────────────────────────────── */
    const activeBody   = document.getElementById('rfq-active-body');
    const activeEmpty  = document.getElementById('rfq-active-empty');
    const activeCount  = document.getElementById('rfq-active-count');
    const activeAction = document.getElementById('rfq-active-action');
    const historyBody  = document.getElementById('rfq-history-body');
    const historyEmpty = document.getElementById('rfq-history-empty');
    const historyLogin = document.getElementById('rfq-history-login');

    /* ── storage helpers ─────────────────────────────────── */
    function getLocal() {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
      catch (e) { return []; }
    }

    function setLocal(items) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      if (window.__RFQ__ && window.__RFQ__.render) window.__RFQ__.render();
    }

    /* ── API helpers ─────────────────────────────────────── */
    async function apiGet(endpoint) {
      if (!syncRoute) return null;
      try {
        const res = await fetch(syncRoute + endpoint, { credentials: 'same-origin' });
        if (!res.ok) return null;
        const data = await res.json();
        return data.ok ? data : null;
      } catch (e) { return null; }
    }

    async function apiPost(endpoint, body) {
      if (!syncRoute) return null;
      try {
        const res = await fetch(syncRoute + endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) return null;
        const data = await res.json();
        return data.ok ? data : null;
      } catch (e) { return null; }
    }

    /* ── debounced sync to server ────────────────────────── */
    let syncTimer = null;
    function debouncedSave(items) {
      if (!loggedIn) return;
      clearTimeout(syncTimer);
      syncTimer = setTimeout(() => {
        apiPost('/save', { items });
      }, 1200);
    }

    /* ── render active items ─────────────────────────────── */
    function renderActive(items) {
      if (!activeBody) return;

      if (!items || items.length === 0) {
        activeBody.innerHTML = '';
        if (activeEmpty) activeEmpty.style.display = '';
        if (activeCount) activeCount.textContent = '';
        if (activeAction) activeAction.style.display = 'none';
        return;
      }

      if (activeEmpty) activeEmpty.style.display = 'none';
      if (activeAction) activeAction.style.display = '';
      if (activeCount) activeCount.textContent = items.length + ' ' + (t.items_count || 'items');

      activeBody.innerHTML = items.map((item, i) => `
        <div class="rfq-proj__item" data-index="${i}">
          <div class="rfq-proj__item-img">
            ${item.img ? '<img src="' + item.img + '" alt="" width="60" height="60" loading="lazy">' : '<div class="rfq-proj__item-placeholder"></div>'}
          </div>
          <div class="rfq-proj__item-info">
            <a href="${item.url || '#'}" class="rfq-proj__item-title">${item.title || ''}</a>
          </div>
          <div class="rfq-proj__item-qty">
            <label class="visually-hidden">${t.quantity || 'Quantity'}</label>
            <input type="number" min="1" max="9999" value="${item.qty || 1}" data-action="qty" data-index="${i}" class="rfq-proj__qty-input">
          </div>
          <button type="button" class="rfq-proj__item-rm" data-action="remove" data-index="${i}" aria-label="${t.remove || 'Remove'}">
            <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M1 1l16 16M17 1L1 17" stroke="currentColor" stroke-width="2"/></svg>
          </button>
        </div>
      `).join('');

      activeBody.onclick = function (e) {
        const btn = e.target.closest('[data-action="remove"]');
        if (btn) {
          const idx = parseInt(btn.dataset.index, 10);
          items.splice(idx, 1);
          setLocal(items);
          debouncedSave(items);
          renderActive(items);
        }
      };

      activeBody.onchange = function (e) {
        const inp = e.target.closest('[data-action="qty"]');
        if (inp) {
          const idx = parseInt(inp.dataset.index, 10);
          const val = Math.max(1, parseInt(inp.value, 10) || 1);
          items[idx].qty = val;
          inp.value = val;
          setLocal(items);
          debouncedSave(items);
        }
      };
    }

    /* ── render history ──────────────────────────────────── */
    function renderHistory(submissions) {
      if (!historyBody) return;

      if (!submissions || submissions.length === 0) {
        historyBody.innerHTML = '';
        if (historyEmpty) historyEmpty.style.display = '';
        return;
      }

      if (historyEmpty) historyEmpty.style.display = 'none';

      historyBody.innerHTML = submissions.map((sub) => {
        const date = new Date(sub.submitted_at);
        const dateStr = date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        const contactName = sub.contact && sub.contact.name ? sub.contact.name : '';
        const itemCount = sub.items ? sub.items.length : 0;
        const itemsHtml = (sub.items || []).map(item => `
          <div class="rfq-hist__detail-item">
            <span class="rfq-hist__detail-title">${item.title || ''}</span>
            <span class="rfq-hist__detail-qty">${t.qty || 'Qty'}: ${item.qty || 1}</span>
          </div>
        `).join('');

        return `
          <div class="rfq-hist__card">
            <div class="rfq-hist__header" data-action="toggle">
              <div class="rfq-hist__meta">
                <span class="rfq-hist__date">${dateStr}</span>
                <span class="rfq-hist__count">${itemCount} ${t.items_count || 'items'}</span>
                ${contactName ? '<span class="rfq-hist__contact">' + contactName + '</span>' : ''}
              </div>
              <div class="rfq-hist__status">
                <span class="rfq-hist__badge">${t.status_submitted || 'Submitted'}</span>
                <svg class="rfq-hist__chevron" width="12" height="12" viewBox="0 0 12 12"><path d="M3 4.5l3 3 3-3" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
              </div>
            </div>
            <div class="rfq-hist__detail" style="display:none">
              ${itemsHtml}
              ${sub.notes ? '<div class="rfq-hist__notes"><strong>' + (t.form_message || 'Notes') + ':</strong> ' + sub.notes + '</div>' : ''}
            </div>
          </div>
        `;
      }).join('');

      historyBody.onclick = function (e) {
        const header = e.target.closest('[data-action="toggle"]');
        if (header) {
          const detail = header.nextElementSibling;
          const chevron = header.querySelector('.rfq-hist__chevron');
          if (detail) {
            const isOpen = detail.style.display !== 'none';
            detail.style.display = isOpen ? 'none' : '';
            if (chevron) chevron.style.transform = isOpen ? '' : 'rotate(180deg)';
          }
        }
      };
    }

    /* ── init ─────────────────────────────────────────────── */
    async function init() {
      let items = getLocal();

      if (loggedIn && syncRoute) {
        const serverData = await apiGet('/state');
        if (serverData && serverData.items && serverData.items.length > 0) {
          if (items.length === 0) {
            items = serverData.items;
            setLocal(items);
          } else {
            const localUrls = new Set(items.map(i => i.url));
            for (const si of serverData.items) {
              if (!localUrls.has(si.url)) {
                items.push(si);
              }
            }
            setLocal(items);
            debouncedSave(items);
          }
        } else if (items.length > 0) {
          debouncedSave(items);
        }
      }

      renderActive(items);

      if (loggedIn && syncRoute) {
        if (historyLogin) historyLogin.style.display = 'none';
        const histData = await apiGet('/history');
        if (histData && histData.submissions) {
          renderHistory(histData.submissions);
        }
      } else {
        if (historyLogin) historyLogin.style.display = '';
        if (historyBody) historyBody.style.display = 'none';
        if (historyEmpty) historyEmpty.style.display = 'none';
      }
    }

    init();
  }

  /* ── Always wait for full DOM ──────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();